import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  Divider,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  ListSubheader,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Task, CaseStage } from '../../types';
import { users } from '../../data/sampleData';
import { caseStages } from '../../data/caseStages';
import { Info } from 'lucide-react';

interface TaskAssignmentFormProps {
  tasks: Task[];
  selectedTasks: Task[];
  taskAssignments: Record<string, string>;
  taskDueDates: Record<string, string>;
  selectedStage: string;
  onStageChange: (stageId: string) => void;
  onTaskToggle: (task: Task) => void;
  onTaskAssignment: (taskId: string, userId: string) => void;
  onTaskDueDate: (taskId: string, date: string) => void;
}

export const TaskAssignmentForm: React.FC<TaskAssignmentFormProps> = ({
  tasks,
  selectedTasks,
  taskAssignments,
  taskDueDates,
  selectedStage,
  onStageChange,
  onTaskToggle,
  onTaskAssignment,
  onTaskDueDate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Group stages by category for the dropdown
  const stagesByCategory = caseStages.reduce((acc, stage) => {
    if (!acc[stage.category]) {
      acc[stage.category] = [];
    }
    acc[stage.category].push(stage);
    return acc;
  }, {} as Record<string, CaseStage[]>);

  // Find current stage and its category
  const currentStage = caseStages.find(s => s.id === selectedStage);
  const currentCategory = currentStage?.category;

  // Get previous and next stages in the same category
  const currentCategoryStages = currentStage 
    ? stagesByCategory[currentStage.category] 
    : [];
  const currentStageIndex = currentCategoryStages.findIndex(s => s.id === selectedStage);
  const previousStage = currentStageIndex > 0 
    ? currentCategoryStages[currentStageIndex - 1] 
    : null;
  const nextStage = currentStageIndex < currentCategoryStages.length - 1 
    ? currentCategoryStages[currentStageIndex + 1] 
    : null;

  const TaskCard = ({ task }: { task: Task }) => {
    const isSelected = selectedTasks.some(t => t.id === task.id);
    
    return (
      <Card 
        sx={{ 
          mb: 2,
          bgcolor: isSelected ? 'action.selected' : 'background.paper',
          transition: 'background-color 0.2s',
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSelected}
                    onChange={() => onTaskToggle(task)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {task.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {task.description}
                    </Typography>
                  </Box>
                }
                sx={{ flex: 1, m: 0 }}
              />
              <Tooltip title={task.description} arrow>
                <IconButton size="small">
                  <Info size={18} />
                </IconButton>
              </Tooltip>
            </Box>

            {isSelected && (
              <Stack spacing={2} sx={{ pl: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Assign To</InputLabel>
                  <Select
                    value={taskAssignments[task.id] || ''}
                    label="Assign To"
                    onChange={(e) => onTaskAssignment(task.id, e.target.value as string)}
                    required
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} ({user.role})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <DatePicker
                  label="Due Date"
                  value={dayjs(taskDueDates[task.id])}
                  onChange={(date) => onTaskDueDate(task.id, date?.toISOString() || new Date().toISOString())}
                  slotProps={{
                    textField: {
                      required: true,
                      fullWidth: true,
                    }
                  }}
                />
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Case Stage</InputLabel>
          <Select
            value={selectedStage}
            label="Case Stage"
            onChange={(e) => onStageChange(e.target.value)}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: isMobile ? '70vh' : 400 },
              },
            }}
          >
            {Object.entries(stagesByCategory).map(([category, stages]) => [
              <ListSubheader key={category}>{category}</ListSubheader>,
              ...stages.map(stage => {
                const isSelected = stage.id === selectedStage;
                const isPrevious = stage.id === previousStage?.id;
                const isNext = stage.id === nextStage?.id;

                return (
                  <MenuItem 
                    key={stage.id} 
                    value={stage.id}
                    sx={{ 
                      pl: 4,
                      position: 'relative',
                      ...(isSelected && {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      }),
                      ...(isPrevious && {
                        borderBottom: '1px dashed',
                        borderBottomColor: 'divider',
                      }),
                      ...(isNext && {
                        borderTop: !isSelected ? '1px dashed' : 'none',
                        borderTopColor: 'divider',
                      }),
                    }}
                  >
                    <Tooltip 
                      title={stage.description}
                      placement="right"
                      arrow
                    >
                      <Box>
                        <Typography 
                          variant="body1"
                          component="span"
                          sx={{
                            fontWeight: isSelected ? 600 : 400,
                          }}
                        >
                          {stage.name}
                        </Typography>
                        {(isPrevious || isNext) && (
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{
                              ml: 1,
                              color: isSelected 
                                ? 'primary.contrastText' 
                                : 'text.secondary',
                              fontStyle: 'italic',
                            }}
                          >
                            ({isPrevious ? 'Previous' : 'Next'} Stage)
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  </MenuItem>
                );
              })
            ]).flat()}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="subtitle2" gutterBottom>
        Select and assign tasks for this case
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which tasks to include and assign team members
      </Typography>

      <Box>
        {tasks.map((task) => (
          <TaskCard key={task.id} task={task} />
        ))}
        {tasks.length === 0 && (
          <Typography 
            color="text.secondary" 
            sx={{ 
              textAlign: 'center', 
              py: 4,
              fontStyle: 'italic',
            }}
          >
            No tasks available for this stage
          </Typography>
        )}
      </Box>
    </Box>
  );
};